import type { CSSProperties } from 'react'
import { useCallback } from 'react'

import {
  type OnlyFansMessage,
  type OnlyFansMessageMedia,
  type VaultMediaFilesDto,
  VaultMediaType,
} from '@api'
import { useUnit } from 'effector-react'

import { AudioPlayer } from '@/components/ui/audio-player'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import {
  $isLoadingMediaAttributes,
  $mediaLabels,
  loadMediaAttributes,
} from '@/models/chattingUI/media-attributes.ts'
import { $creatorId } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs.ts'
import { $mediaFailedAssets } from '@/models/vault/medias/errors.ts'
import { ChatGifMedia } from '@/modules/chatting/ui/chat-window/chat-gif-media'
import { secondsToTime } from '@/modules/chatting/ui/utils'
import { VaultMediaPreview } from '@/modules/vault/ui/media/vault-media-preview'
import { calculateImageSize } from '@/modules/vault/utils'
import { classNames } from '@/utils/helpers.ts'

interface Props {
  medias?: OnlyFansMessageMedia[]
  message: OnlyFansMessage
  isMessageFromFan?: boolean
}

const MAX_MEDIA_WIDTH = 250
const MAX_MEDIA_HEIGHT = 350

export const ChatMessageMedias = ({
  medias,
  message,
  isMessageFromFan,
}: Props) => {
  const creatorId = useUnit($creatorId)
  const mediaFailedAssets = useUnit($mediaFailedAssets)
  const mediaLabels = useUnit($mediaLabels)
  const isLoading = useUnit($isLoadingMediaAttributes)

  const isSentAsPreview = useCallback(
    (mediaId: number) => {
      return message.previews?.includes(mediaId)
    },
    [message.previews],
  )

  const handleLoadMediaAttributes = useCallback((mediaId: number) => {
    loadMediaAttributes(mediaId)
  }, [])

  const handleMediaClick = useCallback(
    (mediaId: number) => {
      if (message.isMediaReady && medias) {
        openDialog({
          type: DIALOG_TYPE.VAULT_MEDIA_PREVIEW,
          mediaList: medias.map(({ id }) => id!),
          mediaId,
        })
      }
    },
    [medias, message.isMediaReady],
  )

  const audioMedias = medias?.filter(
    (media) => media.type === VaultMediaType.AUDIO,
  )

  const graphicMedias = medias?.filter(
    (media) => media.type !== VaultMediaType.AUDIO,
  )

  return (
    <div
      className={classNames(
        'mb-2 flex flex-nowrap items-end overflow-x-scroll',
        {
          'flex-col gap-3':
            message.giphyId || (audioMedias && audioMedias.length > 0),
        },
      )}
    >
      {audioMedias?.map((item) => (
        <AudioPlayer
          media={item}
          key={item.id}
          duration={item.duration!}
          className="w-full min-w-[260px]"
          isMediaReady={!!message.isMediaReady}
          streamingParams={
            isMessageFromFan
              ? {
                  relatedMessageId: message.id,
                  relatedUserId: message.fromUser.id,
                  creatorId: creatorId!,
                  mediaId: item.id,
                }
              : {}
          }
        />
      ))}
      {message.giphyId && (
        <ChatGifMedia
          allowOpenInDialog
          giphyId={message.giphyId}
          className="h-[250px] max-w-full"
        />
      )}
      {graphicMedias?.map((m, _, arr) => {
        let style: CSSProperties = {}
        const failedAsset =
          !m.files.thumb?.url || mediaFailedAssets[m.files.thumb.url]

        if (
          !failedAsset &&
          !!m.files.thumb &&
          arr.length === 1 &&
          message.isMediaReady
        ) {
          const originalWidth = m.files.thumb.width
          const originalHeight = m.files.thumb.height

          const calculatedPlaceHolderSize = calculateImageSize(
            originalWidth,
            originalHeight,
            MAX_MEDIA_WIDTH,
            MAX_MEDIA_HEIGHT,
          )

          style = {
            width: calculatedPlaceHolderSize?.width,
            height: calculatedPlaceHolderSize?.height,
          }
        }

        return (
          <div
            key={m.id}
            onClick={() => handleMediaClick(m.id!)}
            className={classNames(
              'relative mr-2 flex-shrink-0 overflow-hidden last:mr-0',
              {
                'cursor-pointer': message.isMediaReady,
                'h-[144px] w-[144px]': medias && medias.length > 1,
                'flex flex-1': medias && medias.length === 1,
              },
            )}
            style={style}
          >
            <VaultMediaPreview
              files={m.files as VaultMediaFilesDto}
              type={m.type as VaultMediaType}
              fit={arr.length > 1 ? 'cover' : 'contain'}
              isReady={message.isMediaReady}
              isFree={isSentAsPreview(m.id!)}
              // @ts-ignore
              hasError={m.hasError}
              title={mediaLabels[m.id!] || ''}
              isLoading={isLoading}
              onMouseEnter={() => handleLoadMediaAttributes(m.id!)}
            />
            {m.type === VaultMediaType.VIDEO && (
              <div className="absolute bottom-0 left-0 flex w-full items-center justify-start bg-gradient-to-t from-black/50 px-2 py-2">
                <span className="text-xs font-medium text-white">
                  {secondsToTime(Math.floor(m.duration!))}
                </span>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
